import { toast } from "@/components/ui/sonner";
import {
  approveLicenseMutation,
  rejectLicenseMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { notifyLicenseDetailsChanged } from "@/lib/license-details-change-notification";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

export function useLicenseHandlers() {
  const queryClient = useQueryClient();

  // Dialog state
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [pendingLicenseId, setPendingLicenseId] = useState<string | null>(null);

  const approveMutation = useMutation({
    ...approveLicenseMutation(),
  });

  const rejectMutation = useMutation({
    ...rejectLicenseMutation(),
  });

  const showApproveDialog = (licenseId: string) => {
    setPendingLicenseId(licenseId);
    setIsApproveDialogOpen(true);
  };

  const showRejectDialog = (licenseId: string) => {
    setPendingLicenseId(licenseId);
    setIsRejectDialogOpen(true);
  };

  const handleApproveLicense = async (comment: string, isPublic = false) => {
    if (!pendingLicenseId) return;

    try {
      await approveMutation.mutateAsync({
        path: {
          license_id: pendingLicenseId,
        },
        body: {
          note: comment || null,
          is_note_public: isPublic,
        },
      });
      await notifyLicenseDetailsChanged(queryClient, pendingLicenseId);
      if (isPublic && comment.trim()) {
        await queryClient.invalidateQueries({
          queryKey: ["listLicenseNotificationEmails", pendingLicenseId],
        });
      }
      toast({
        title: "Success",
        description: "License approved successfully",
        variant: "secondary",
      });
      setIsApproveDialogOpen(false);
      setPendingLicenseId(null);
    } catch (error) {
      toast({
        title: "Error",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleRejectLicense = async (comment: string, isPublic = false) => {
    if (!pendingLicenseId) return;

    try {
      await rejectMutation.mutateAsync({
        path: {
          license_id: pendingLicenseId,
        },
        body: {
          note: comment || null,
          is_note_public: isPublic,
        },
      });
      await notifyLicenseDetailsChanged(queryClient, pendingLicenseId);
      if (isPublic && comment.trim()) {
        await queryClient.invalidateQueries({
          queryKey: ["listLicenseNotificationEmails", pendingLicenseId],
        });
      }
      toast({
        title: "Success",
        description: "License rejected successfully",
        variant: "secondary",
      });
      setIsRejectDialogOpen(false);
      setPendingLicenseId(null);
    } catch (error) {
      toast({
        title: "Error",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  return {
    handleApproveLicense,
    handleRejectLicense,
    showApproveDialog,
    showRejectDialog,
    isApproveDialogOpen,
    setIsApproveDialogOpen,
    isRejectDialogOpen,
    setIsRejectDialogOpen,
    isApproveLoading: approveMutation.isPending,
    isRejectLoading: rejectMutation.isPending,
  };
}
